// PROJECT-WORK read-time normalizer for LEGACY service_type strings.
// 2026-07-23 (P1 T3). 2026-08-18 (Run A): the entry vocabulary moved to the
// database.
//
// The exported constant that used to double as the entry dropdown
// (PROJECT_WORK_SERVICE_TYPES) is GONE. New project work is entered against
// service_divisions + service_catalog, read live from the DB — see
// hooks/use-service-catalog.ts — so the list can no longer drift from what the
// database will accept. That drift is exactly how service_type went from 56
// free-text variants to 77 AFTER a dropdown had already shipped.
//
// What survives here is READ-TIME ONLY: NORMALIZED_LEGACY_BUCKETS is the output
// domain of normalizeProjectServiceType(), which folds the historic free text on
// projects.service_type_raw onto a small set of buckets so old activity groups
// cleanly. It is never offered as an input choice and never written anywhere.

export const NORMALIZED_LEGACY_BUCKETS = [
  'Auto Scrub',
  'Strip & Wax',
  'Scrub & Recoat / Buff',
  'Carpet Cleaning',
  'Window Cleaning',
  'Deep Clean',
  'Floor Work (other)',
  'Lawn / Landscaping',
  'Trash / Day Porter',
  'Staffing Coverage',
  'Other',
] as const;

export type ProjectWorkServiceType = (typeof NORMALIZED_LEGACY_BUCKETS)[number];

const OTHER: ProjectWorkServiceType = 'Other';

/**
 * True when `v` is one of the legacy normalizer buckets EXCLUDING "Other".
 * Read-time only — used when deciding how to LABEL a historic raw value, never
 * to decide what a user may enter.
 */
export function isCanonicalProjectServiceType(v: string | null | undefined): boolean {
  return (
    !!v &&
    v !== OTHER &&
    (NORMALIZED_LEGACY_BUCKETS as readonly string[]).includes(v)
  );
}

/**
 * Fold any raw legacy service_type string onto one of the 11 buckets.
 * Case / spacing / punctuation / synonym-insensitive; unmatched → "Other".
 * Read-time only — never used to overwrite stored values.
 *
 * Rule order is significant (most specific first): a "Floor Strip & Wax" must
 * land on Strip & Wax before the generic "floor" rule, and "Scrubbing/
 * Burnishing" on Scrub & Recoat / Buff before the generic "scrub" rule.
 */
export function normalizeProjectServiceType(
  raw: string | null | undefined,
): ProjectWorkServiceType {
  if (!raw) return OTHER;
  const s = raw
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return OTHER;
  const any = (...words: string[]) => words.some((w) => s.includes(w));

  if (any('strip', 'stip')) return 'Strip & Wax'; // strip/stip (typo) and wax
  if (any('recoat', 'buff', 'burnish')) return 'Scrub & Recoat / Buff';
  if (any('auto scrub', 'autoscrub', 'scrub')) return 'Auto Scrub';
  if (any('carpet')) return 'Carpet Cleaning';
  if (any('window')) return 'Window Cleaning';
  if (any('lawn', 'landscap')) return 'Lawn / Landscaping';
  if (any('trash', 'garbage', 'porter')) return 'Trash / Day Porter';
  if (any('staffing', 'staff cover', 'coverage')) return 'Staffing Coverage';
  if (any('deep clean')) return 'Deep Clean';
  if (any('floor')) return 'Floor Work (other)';
  return OTHER;
}
